/** The What's new dialog's decisions, as pure functions.
 *
 *  The dialog is the second half of one flow with the update banner
 *  (MaintenanceBanner): while the server runs another build the banner
 *  asks for a reload and the dialog waits; after the reload the tab runs
 *  the new build and the dialog says what changed since the user's last
 *  visit. The server does the filtering (audience, deployment features,
 *  language, collapsed fixes); this module decides whether to open,
 *  acknowledge silently or only show the navbar dot, and how to fit a
 *  long window into one dialog. */
import type { ReleaseNote, ReleaseNoteKind, ReleaseNotesPage, WhatsNew } from "@/api/types";
import { compareSemver, parseSemver } from "@/lib/semver";

export { compareSemver };

/** Everything the dialog must wait for. */
export type GateInput = {
  /** ``/auth/me`` answered: the user is known. */
  meReady: boolean;
  /** ``/api/version`` answered: the server's state is known. */
  versionReady: boolean;
  /** The build this tab runs (``tabBuild()``), or null while unknown. */
  tabVersion: string | null;
  /** The server runs another build: the banner asks for a reload, and
   *  this tab doesn't have the new features yet. */
  updateAvailable: boolean;
  unreachable: boolean;
  /** A deploy is under way. */
  strictMaintenance: boolean;
  online: boolean;
  /** The tab is visible. A hidden tab asks once it's shown, by which
   *  time another tab may have acknowledged. */
  visible: boolean;
};

/** Whether the What's new query may run (and so the dialog open). */
export function whatsNewGate(input: GateInput): boolean {
  const tab = parseSemver(input.tabVersion);
  const versioned = tab !== null && tab.some((part) => part > 0);
  return (
    input.meReady &&
    input.versionReady &&
    versioned &&
    !input.updateAvailable &&
    !input.unreachable &&
    !input.strictMaintenance &&
    input.online &&
    input.visible
  );
}

/** What to do with a What's new answer:
 *
 *  * ``none``: nothing. The build has no notes (acknowledging would burn
 *    the user's window), or the release-notes page is open and
 *    acknowledges itself.
 *  * ``ack``: record ``current_version`` silently (a first visit, or
 *    nothing new).
 *  * ``dialog``: open the dialog.
 *  * ``dot``: only fixes. No dialog and no acknowledgement: the navbar
 *    dot shows, and the fixes fold into the next dialog. */
export type Decision = "none" | "ack" | "dialog" | "dot";

export function decide(data: WhatsNew, pathname: string): Decision {
  if (!data.index_ok) return "none";
  if (data.first_visit) return "ack";
  if (data.notes.length === 0) return "ack";
  if (pathname === "/app/changelog" || pathname.startsWith("/app/changelog/")) return "none";
  if (data.notes.some((n) => n.highlight || n.kind !== "fixed")) return "dialog";
  return "dot";
}

export const HIGHLIGHT_CAP = 3;
/** Highlights and list items together. */
export const ITEM_CAP = 8;
export const FIX_CAP = 10;

const KIND_ORDER: Record<ReleaseNoteKind, number> = { new: 0, changed: 1, improved: 2, fixed: 3 };

export type DialogPlan = {
  /** Up to three, newest first, with bodies. */
  highlights: ReleaseNote[];
  /** The other new, changed and improved notes, by kind then newest. */
  list: ReleaseNote[];
  /** Behind "Show N fixes", newest first. */
  fixes: ReleaseNote[];
  /** Not rendered at all: "And N more changes." */
  more: number;
  total: number;
};

/** Fit a window (newest release first, as the server sends it) into
 *  one dialog: highlights, then a list of titles, then fixes behind a
 *  disclosure, then a count of the rest. */
export function planDialog(notes: readonly ReleaseNote[], omitted: number): DialogPlan {
  const highlights = notes.filter((n) => n.highlight).slice(0, HIGHLIGHT_CAP);
  const rest = notes.filter((n) => !highlights.includes(n));
  const list = rest
    .filter((n) => n.kind !== "fixed")
    // Stable: newest first within a kind, as the input is ordered.
    .sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind])
    .slice(0, Math.max(0, ITEM_CAP - highlights.length));
  const fixes = rest.filter((n) => n.kind === "fixed").slice(0, FIX_CAP);
  const total = notes.length + Math.max(0, omitted);
  return {
    highlights,
    list,
    fixes,
    more: total - highlights.length - list.length - fixes.length,
    total,
  };
}

/** A release day, ``YYYY-MM-DD``, as a local calendar date.
 *  ``new Date("2026-09-25")`` is UTC midnight: the 24th west of UTC. */
export function parseDay(day: string): Date {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** The first and last release day among ``notes``. */
export function noteDateRange(notes: readonly ReleaseNote[]): { from: string; to: string } | null {
  if (notes.length === 0) return null;
  const days = notes.map((n) => n.date).sort();
  return { from: days[0]!, to: days[days.length - 1]! };
}

/** "2 October 2026" in ``locale`` (pass ``intlLocale()``). */
export function formatDay(day: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", year: "numeric" }).format(
    parseDay(day),
  );
}

/** "16–27 September" in ``locale`` (pass ``intlLocale()``). */
export function formatDayRange(from: string, to: string, locale: string): string {
  const format = new Intl.DateTimeFormat(locale, { day: "numeric", month: "long" });
  return format.formatRange(parseDay(from), parseDay(to));
}

export type Release = ReleaseNotesPage["releases"][number];
export type DayGroup = { date: string; releases: Release[] };

/** Consecutive releases of the same day as one group (several releases
 *  a day read better as days). Pages are concatenated first, so a day
 *  that continues on the next page stays one group. */
export function groupReleasesByDay(releases: readonly Release[]): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const release of releases) {
    const last = groups[groups.length - 1];
    if (last && last.date === release.date) last.releases.push(release);
    else groups.push({ date: release.date, releases: [release] });
  }
  return groups;
}
